/**
 * Unified Role-Based Access Control (RBAC) - Frontend
 * 
 * IMPORTANT: This system uses a single unified admin role: 'admin'
 * - Database stores: admin, supervisor, technician
 * - Frontend displays: admin (shown as 'مدير النظام' in Arabic)
 * - No 'system_admin' - use 'admin' only
 * 
 * This file provides utilities for frontend role handling.
 * The backend normalizeRole function handles legacy 'system_admin' 
 * conversion automatically, so we just need to use 'admin' consistently.
 */

export const ROLE = Object.freeze({
  ADMIN: 'admin',
  SUPERVISOR: 'supervisor',
  TECHNICIAN: 'technician',
  VIEWER: 'viewer'
});

export const TICKET_ASSIGNABLE_ROLES = Object.freeze([
  ROLE.TECHNICIAN,
  ROLE.SUPERVISOR,
  ROLE.ADMIN
]);

/**
 * Normalize role to lowercase underscore format
 * Returns consistent 'admin' (never 'system_admin')
 */
export const normalizeRole = (role) => {
  if (!role) return ROLE.VIEWER;
  const normalized = String(role).trim().toLowerCase().replace(/\s+/g, '_');
  
  // Handle legacy system_admin - normalize to admin
  if (normalized === 'system_admin') {
    return ROLE.ADMIN;
  }
  
  // Return normalized role if it's a known role
  const knownRoles = [ROLE.ADMIN, ROLE.SUPERVISOR, ROLE.TECHNICIAN, ROLE.VIEWER];
  if (knownRoles.includes(normalized)) {
    return normalized;
  }
  
  // Unknown role - return as-is for flexibility
  return normalized;
};

/**
 * Check if role has admin privileges
 * Works with both 'admin' and legacy 'system_admin' inputs
 */
export const isAdminLike = (role) => {
  const normalized = normalizeRole(role);
  return normalized === ROLE.ADMIN;
};

/**
 * Check if role is supervisor or admin
 */
export const isSupervisorOrAbove = (role) => {
  const normalized = normalizeRole(role);
  return normalized === ROLE.ADMIN || normalized === ROLE.SUPERVISOR;
};

/**
 * Check if role is a viewer (read-only) user
 */
export const isViewer = (role) => {
  const normalized = normalizeRole(role);
  return normalized === ROLE.VIEWER;
};

/**
 * Check if role can perform write operations on tickets
 * (technician and above, but not viewer)
 */
export const canWriteTickets = (role) => {
  const normalized = normalizeRole(role);
  return normalized === ROLE.ADMIN || 
         normalized === ROLE.SUPERVISOR || 
         normalized === ROLE.TECHNICIAN;
};

/**
 * Check if role can access reports (view, print, export)
 */
export const canAccessReports = (role) => {
  const normalized = normalizeRole(role);
  // Viewers can access all reports (view, print, export)
  // Technicians can access dashboard
  // Supervisors and Admins can access all reports
  return normalized === ROLE.VIEWER ||
         normalized === ROLE.TECHNICIAN ||
         normalized === ROLE.SUPERVISOR ||
         normalized === ROLE.ADMIN;
};

/**
 * Check if role can create/modify data (not viewer)
 */
export const canModifyData = (role) => {
  const normalized = normalizeRole(role);
  return normalized !== ROLE.VIEWER;
};

/**
 * Get roles that can be assigned by an actor with the given role
 */
export const getAssignableRolesForActor = (actorRole) => {
  const role = normalizeRole(actorRole);

  if (isAdminLike(role)) {
    return [ROLE.TECHNICIAN, ROLE.SUPERVISOR, ROLE.ADMIN];
  }

  if (role === ROLE.SUPERVISOR) {
    return [ROLE.TECHNICIAN, ROLE.SUPERVISOR];
  }

  return [];
};

export const getTicketAssignableRolesForActor = (actorRole) => {
  const role = normalizeRole(actorRole);

  if (isAdminLike(role) || role === ROLE.SUPERVISOR) {
    return [...TICKET_ASSIGNABLE_ROLES];
  }

  if (role === ROLE.TECHNICIAN) {
    return [ROLE.TECHNICIAN];
  }

  return [];
};

export const isTicketAssignableRole = (role) => {
  const normalized = normalizeRole(role);
  return TICKET_ASSIGNABLE_ROLES.includes(normalized);
};

/**
 * Role labels for display in Arabic
 */
export const ROLE_LABELS = {
  admin: 'مدير النظام',
  supervisor: 'مشرف',
  technician: 'فني',
  viewer: 'مستخدم عرض'
};

/**
 * Badge variants for different roles
 */
export const ROLE_BADGE_VARIANTS = {
  admin: 'danger',
  supervisor: 'warning',
  technician: 'info',
  viewer: 'secondary'
};

/**
 * Get display label for a role
 */
export const getRoleLabel = (role) => {
  const normalized = normalizeRole(role);
  return ROLE_LABELS[normalized] || ROLE_LABELS.viewer;
};

/**
 * Get badge variant for a role
 */
export const getRoleBadgeVariant = (role) => {
  const normalized = normalizeRole(role);
  return ROLE_BADGE_VARIANTS[normalized] || ROLE_BADGE_VARIANTS.viewer;
};

/**
 * Check if role can rate technician performance
 */
export const canRateTechnician = (role) => {
  const normalized = normalizeRole(role);
  return normalized === ROLE.ADMIN || normalized === ROLE.SUPERVISOR;
};

/**
 * Check if role can manage exceptional work
 */
export const canManageExceptionalWork = (role) => {
  const normalized = normalizeRole(role);
  return normalized === ROLE.ADMIN || normalized === ROLE.SUPERVISOR;
};

/**
 * Check if role can approve exceptional work
 */
export const canApproveExceptionalWork = (role) => {
  const normalized = normalizeRole(role);
  return normalized === ROLE.ADMIN || normalized === ROLE.SUPERVISOR;
};
